import { useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { SmilePlus, TrendingUp, TrendingDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { toast } from 'sonner';

const SURVEY_LABELS = {
  control: 'Sense of Control',
  productivity: 'Productivity',
  efficiency: 'Efficiency',
  contentment: 'Contentment',
  self_esteem: 'Self-esteem',
  concentration: 'Concentration',
  work_life_balance: 'Work-Life Balance'
};

const TOOLTIP_STYLE = {
  backgroundColor: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: '8px'
};

const DEFAULT_SURVEY = {
  control: 5, productivity: 5, efficiency: 5, contentment: 5,
  self_esteem: 5, concentration: 5, work_life_balance: 5
};

export const MoodMonitorCard = ({ wellbeingSurveys, onSurveysRefresh }) => {
  const [showSurveyDialog, setShowSurveyDialog] = useState(false);
  const [surveyData, setSurveyData] = useState(DEFAULT_SURVEY);

  const handleSubmitSurvey = async () => {
    try {
      await api.post('/surveys/wellbeing', surveyData);
      toast.success('Thank you for your feedback!');
      setShowSurveyDialog(false);
      onSurveysRefresh();
    } catch { toast.error('Failed to submit survey'); }
  };

  const chartData = Object.entries(SURVEY_LABELS).map(([key, label]) => ({
    metric: label.replace('Work-Life Balance', 'Work-Life').replace('Sense of Control', 'Control'),
    Latest: wellbeingSurveys[0]?.[key] ?? 0,
    ...(wellbeingSurveys.length > 1 ? { Previous: wellbeingSurveys[1]?.[key] ?? 0 } : {})
  }));

  return (
    <>
      <Card className="glass border-white/20 dark:border-white/10">
        <CardHeader>
          <CardTitle className="font-['Manrope'] text-lg flex items-center gap-2">
            <SmilePlus className="w-5 h-5" /> Mood Monitor
          </CardTitle>
          <CardDescription>
            {wellbeingSurveys.length > 0
              ? `Last recorded ${new Date(wellbeingSurveys[0].submitted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
              : 'No mood data yet'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {wellbeingSurveys.length > 0 ? (
            <div className="space-y-4">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="metric" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={50} />
                  <YAxis domain={[0, 10]} tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  {wellbeingSurveys.length > 1 && <Bar dataKey="Previous" fill="#94a3b8" radius={[4, 4, 0, 0]} barSize={16} />}
                  <Bar dataKey="Latest" fill="#10b981" radius={[4, 4, 0, 0]} barSize={16} />
                  {wellbeingSurveys.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
                </BarChart>
              </ResponsiveContainer>

              {wellbeingSurveys.length > 1 && (
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(SURVEY_LABELS).map(([key, label]) => {
                    const diff = (wellbeingSurveys[0]?.[key] ?? 0) - (wellbeingSurveys[1]?.[key] ?? 0);
                    if (diff === 0) return null;
                    return (
                      <div key={key} className="flex items-center gap-1.5 text-xs">
                        {diff > 0 ? <TrendingUp className="w-3 h-3 text-emerald-500" /> : <TrendingDown className="w-3 h-3 text-rose-500" />}
                        <span className={diff > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}>
                          {label}: {diff > 0 ? '+' : ''}{diff}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              <Button variant="outline" className="w-full gap-2" onClick={() => setShowSurveyDialog(true)} data-testid="update-mood-btn">
                <SmilePlus className="w-4 h-4" /> Update Current Mood
              </Button>
            </div>
          ) : (
            <div className="h-[250px] flex flex-col items-center justify-center gap-4">
              <p className="text-muted-foreground text-sm">Record your first mood check</p>
              <Button variant="outline" className="gap-2" onClick={() => setShowSurveyDialog(true)} data-testid="first-mood-btn">
                <SmilePlus className="w-4 h-4" /> Record Mood
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showSurveyDialog} onOpenChange={setShowSurveyDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Update Your Mood</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-6 max-h-[60vh] overflow-y-auto">
            <p className="text-sm text-muted-foreground">
              Please rate how you feel (0 = Very Low, 10 = Very High)
            </p>
            {Object.entries(SURVEY_LABELS).map(([key, label]) => (
              <div key={key} className="space-y-2">
                <div className="flex justify-between">
                  <Label>{label}</Label>
                  <span className="text-sm font-medium">{surveyData[key]}</span>
                </div>
                <Slider
                  value={[surveyData[key]]}
                  onValueChange={([value]) => setSurveyData(prev => ({ ...prev, [key]: value }))}
                  max={10} step={1} className="w-full"
                  data-testid={`survey-slider-${key}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSurveyDialog(false)}>Later</Button>
            <Button onClick={handleSubmitSurvey} data-testid="submit-survey-btn">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
